import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModalContent } from "./styles";
import Typography from "../Typography";
import { useLayers } from "../../context/LayersContext";
import { useLayerPermissions } from "../../hooks/useLayerPermissions";
import { mockLayers } from "../../global/constants/layers";
import { Wind, Sun, SlidersHorizontal, Info } from "lucide-react";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Tooltip,
  Box,
} from "@mui/material";

const SCENARIO_BLUE = "#0C2856";

type Scenario =
  | "teorico"
  | "geografico"
  | "tecnico"
  | "economico1"
  | "economico2"
  | "economico3";

type EnergyType = "eolico" | "solar";

const SOLAR_PROJECTION_LAYERS: Partial<Record<Scenario, string>> = {
  teorico: "pepe-cog:potencial_teorico_solar",
  geografico: "pepe-cog:potencial_geografico_solar_PE",
  tecnico: "pepe-cog:potencial_tecnico_solar_PE",
  economico1: "pepe-cog:potencial_economico1_solar_PE",
  economico2: "pepe-cog:potencial_economico2_solar_PE",
  economico3: "pepe-cog:potencial_economico3_solar_PE",
};

const ECONOMICO2_COMPANION_LAYERS = ["69 kV", "138 kV", "Subestações da rede de distribuição"];

const EOLICO_PROJECTION_LAYERS: Partial<Record<Scenario, string>> = {
  teorico: "pepe-cog:potencial_teorico_eolico",
  geografico: "pepe-cog:potencial_geografico_eolico_PE",
  economico1: "pepe-cog:potencial_economico1_eolico_PE",
};

const PROJECTION_LAYERS: Record<EnergyType, Partial<Record<Scenario, string>>> = {
  solar: SOLAR_PROJECTION_LAYERS,
  eolico: EOLICO_PROJECTION_LAYERS,
};

const BOUNDARY_LAYERS = ["pepe-atlas:boundary_30km", "pepe-atlas:boundary_100km"];

const ALL_PROJECTION_TITLES = [
  ...Object.values(SOLAR_PROJECTION_LAYERS),
  ...Object.values(EOLICO_PROJECTION_LAYERS),
];

// Premissas de cada cenário econômico, extraídas do catálogo de restrições da API
// (atlas-api: app/domain/restrictions/catalog.py). Pendentes de validação com a
// Aeroespacial — ao alterar aqui, mantenha o catálogo da API em sincronia.
const SCENARIO_PREMISES: Partial<Record<Scenario, string[]>> = {
  economico1: [
    "Solar: área do cenário Técnico a até 30 km das linhas de transmissão de 138/230/500 kV.",
    "Eólico: área do cenário Geográfico a até 100 km das linhas de transmissão de 138/230/500 kV.",
  ],
  economico2: [
    "Exclusivo para Solar.",
    "Área do cenário Econômico I a até 10 km das linhas de subtransmissão (rede de distribuição).",
  ],
  economico3: [
    "Exclusivo para Solar.",
    "Não introduz restrição nova: recombina as máscaras dos níveis anteriores — declividade acima de 8º, corpos d'água, florestas, áreas agrícolas, zonas urbanas com faixa de 500 m e áreas de preservação.",
  ],
};

const SCENARIOS: { value: Scenario; label: string }[] = [
  { value: "teorico", label: "Teórico" },
  { value: "geografico", label: "Geográfico" },
  { value: "tecnico", label: "Técnico" },
  { value: "economico1", label: "Econômico I (Rede Básica)" },
  { value: "economico2", label: "Econômico II (Rede de Distribuição)" },
  { value: "economico3", label: "Econômico III" },
];

const SOLAR_ONLY_SCENARIOS: Scenario[] = ["tecnico", "economico2", "economico3"];

function getEnergyConfig(scenario: Scenario | null): {
  value: string[];
  disabled: boolean;
  tooltip: string;
} {
  if (!scenario) {
    return { value: [], disabled: true, tooltip: "" };
  }
  if (SOLAR_ONLY_SCENARIOS.includes(scenario)) {
    return {
      value: ["solar"],
      disabled: true,
      tooltip:
        scenario === "tecnico"
          ? "O cenário Técnico é exclusivo para Solar"
          : "Os cenários Econômico II e III são exclusivos para Solar",
    };
  }
  return { value: [], disabled: false, tooltip: "" };
}

// Camadas que acompanham a projeção de um cenário/fonte (rede, raio de distância).
function companionLayers(scenario: Scenario, energy: EnergyType): string[] {
  if (scenario === "economico1") {
    return [
      "Linhas de Transmissão em Operação",
      "Subestações em Operação (SIN - Rede Básica)",
      energy === "solar" ? "pepe-atlas:boundary_30km" : "pepe-atlas:boundary_100km",
    ];
  }
  if (scenario === "economico2") return ECONOMICO2_COMPANION_LAYERS;
  return [];
}

export default function ScenariosModal() {
  const { cleanCheckedLayers, addNewLayer, layers } = useLayers();
  const { canViewLayer } = useLayerPermissions();
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [energyType, setEnergyType] = useState<EnergyType | null>(null);
  const navigate = useNavigate();

  // Verifica permissão pelo layerName real — mesma lógica do LayersModal.
  // Aceita tanto "workspace:layerName" quanto o título de exibição (ex.: as camadas
  // companheiras "69 kV" / "Subestações da rede de distribuição"), resolvendo o
  // título para o layerName real via mockLayers antes de checar a permissão.
  const canAddLayer = (workspaceLayerNameOrTitle: string): boolean => {
    const byTitle = mockLayers.find((l) => l.title === workspaceLayerNameOrTitle);
    const layerName = byTitle
      ? byTitle.layerName
      : workspaceLayerNameOrTitle.split(":")[1] ?? workspaceLayerNameOrTitle;
    return canViewLayer(layerName);
  };

  // Troca a projeção ativa preservando as demais camadas que o usuário já ligou.
  const showProjection = (
    scenario: Scenario,
    energy: EnergyType,
    currentLayers: string[]
  ) => {
    const base = currentLayers.filter(
      (l) => !BOUNDARY_LAYERS.includes(l) && !ALL_PROJECTION_TITLES.includes(l)
    );
    const projectionLayer = PROJECTION_LAYERS[energy][scenario];
    const toAdd = [
      ...companionLayers(scenario, energy),
      ...(projectionLayer ? [projectionLayer] : []),
    ].filter(canAddLayer);
    addNewLayer(Array.from(new Set([...base, ...toAdd])));
  };

  // Ao escolher um cenário a projeção já entra no mapa (Solar por padrão) —
  // antes o mapa ficava em branco até o usuário escolher a fonte de energia.
  const handleScenarioChange = (value: Scenario) => {
    cleanCheckedLayers();
    setSelectedScenario(value);
    setEnergyType("solar");
    showProjection(value, "solar", []);
  };

  const energyConfig = getEnergyConfig(selectedScenario);
  const displayValue = energyConfig.disabled
    ? energyConfig.value
    : energyType
      ? [energyType]
      : [];

  const handleEnergyClick = (energy: EnergyType) => {
    if (energyConfig.disabled || !selectedScenario) return;
    setEnergyType(energy);
    showProjection(selectedScenario, energy, layers);
  };

  const showTooltip = energyConfig.disabled && !!selectedScenario && !!energyConfig.tooltip;

  return (
    <ModalContent>
      <Typography variant="subtitle1" color="#080D1A">
        Projeções
      </Typography>

      <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }}>
        <RadioGroup
          value={selectedScenario ?? ""}
          onChange={(e) => handleScenarioChange(e.target.value as Scenario)}
        >
          {SCENARIOS.map(({ value, label }) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio size="small" />}
              label={
                SCENARIO_PREMISES[value] ? (
                  <span className="flex items-center gap-1.5">
                    {label}
                    <Tooltip
                      arrow
                      placement="right"
                      title={
                        <Box>
                          <Box sx={{ fontWeight: 600, mb: 0.5 }}>Premissas do cenário</Box>
                          {SCENARIO_PREMISES[value]!.map((premise) => (
                            <Box key={premise} sx={{ mb: 0.5 }}>
                              • {premise}
                            </Box>
                          ))}
                        </Box>
                      }
                    >
                      <Info
                        size={14}
                        color={SCENARIO_BLUE}
                        aria-label={`Premissas do cenário ${label}`}
                      />
                    </Tooltip>
                  </span>
                ) : (
                  label
                )
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <Tooltip
          title={energyConfig.tooltip}
          arrow
          placement="top"
          disableHoverListener={!showTooltip}
          disableFocusListener={!showTooltip}
          disableTouchListener={!showTooltip}
        >
          <span style={{ display: "block" }}>
            <div
              className="flex w-full rounded-xl border border-gray-200 bg-white"
              style={{ opacity: energyConfig.disabled ? 0.7 : 1 }}
            >
              <button
                onClick={() => handleEnergyClick("solar")}
                className={[
                  "flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm border-none cursor-pointer transition-all duration-150 bg-white rounded-l-xl",
                  displayValue.includes("solar")
                    ? "font-semibold text-[#0C2856] border-b-2 border-[#0C2856]"
                    : "font-normal text-gray-400 border-b-2 border-transparent",
                ].join(" ")}
              >
                <Sun size={20} color="#636467" />
                Energia Solar
              </button>
              <div className="w-px bg-gray-200 self-stretch" />
              <button
                onClick={() => handleEnergyClick("eolico")}
                className={[
                  "flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm border-none cursor-pointer transition-all duration-150 bg-white rounded-r-xl",
                  displayValue.includes("eolico")
                    ? "font-semibold text-[#0C2856] border-b-2 border-[#0C2856]"
                    : "font-normal text-gray-400 border-b-2 border-transparent",
                ].join(" ")}
              >
                <Wind size={20} color="#636467" />
                Energia Eólica
              </button>
            </div>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mt: 3 }}>
        <button
          onClick={() => navigate("/projecao-customizada")}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-[#0C2856] text-[#0C2856] text-sm font-medium hover:bg-[#0C2856] hover:text-white transition-colors"
        >
          <SlidersHorizontal size={16} />
          Projeção customizada
        </button>
      </Box>
    </ModalContent>
  );
}
